
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Loader2, LogIn, UserPlus, User, Key } from 'lucide-react';
import ConnectionStatus from '@/components/ConnectionStatus';

const Auth: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn, signUp, user } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsProcessing(true);

    try {
      if (isLoggingIn) {
        await signIn(email, password);
      } else {
        await signUp(email, password, { full_name: fullName });
        // Switch to login tab after successful signup
        setIsLoggingIn(true);
      }
    } catch (err: any) {
      setError(err.message || (language === 'ur' ? 'ایک خرابی پیش آگئی' : 'An error occurred'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className={`flex justify-center items-center min-h-screen p-4 ${language === 'ur' ? 'rtl' : 'ltr'}`}>
      <ConnectionStatus className="absolute top-2 right-2" />
      
      <Card className={`w-full max-w-md ${isMobile ? 'border-0 shadow-none' : 'shadow-lg'}`}>
        <CardHeader className={`text-center ${isMobile ? 'pb-2' : 'pb-6'}`}>
          <CardTitle className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>
            {language === 'ur' ? 'عدالت' : 'Adaalat'}
          </CardTitle>
          <CardDescription>
            {language === 'ur' 
              ? 'اپنے قانونی دستاویزات کو محفوظ کرنے کے لیے لاگ ان کریں' 
              : 'Sign in to access and save your legal documents'}
          </CardDescription>
        </CardHeader>
        
        <Tabs defaultValue={isLoggingIn ? "login" : "signup"} 
              onValueChange={(value) => setIsLoggingIn(value === "login")}
              className="w-full">
          <TabsList className="grid grid-cols-2 mb-4">
            <TabsTrigger value="login" className="data-[state=active]:font-semibold">
              <LogIn className="mr-2 h-4 w-4" />
              {language === 'ur' ? 'لاگ ان' : 'Login'}
            </TabsTrigger>
            <TabsTrigger value="signup" className="data-[state=active]:font-semibold">
              <UserPlus className="mr-2 h-4 w-4" />
              {language === 'ur' ? 'اکاؤنٹ بنائیں' : 'Sign Up'}
            </TabsTrigger>
          </TabsList>
          
          <form onSubmit={handleSubmit}>
            <CardContent className={`space-y-4 ${isMobile ? 'pb-2' : 'pb-6'}`}>
              {!isLoggingIn && (
                <div className="space-y-2">
                  <label htmlFor="fullName" className="text-sm font-medium flex items-center">
                    <User className="mr-2 h-4 w-4" />
                    {language === 'ur' ? 'پورا نام' : 'Full Name'}
                  </label>
                  <Input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={language === 'ur' ? 'اپنا پورا نام درج کریں' : 'Enter your full name'}
                    required={!isLoggingIn}
                    disabled={isProcessing}
                    className="w-full"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="mr-2 h-4 w-4">
                    <path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" />
                    <path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" />
                  </svg>
                  {language === 'ur' ? 'ای میل' : 'Email'}
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={language === 'ur' ? 'ای میل درج کریں' : 'Enter your email'}
                  required
                  disabled={isProcessing}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium flex items-center">
                  <Key className="mr-2 h-4 w-4" />
                  {language === 'ur' ? 'پاسورڈ' : 'Password'}
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={language === 'ur' ? 'پاسورڈ درج کریں' : 'Enter your password'}
                  required
                  disabled={isProcessing}
                  className="w-full"
                  minLength={6}
                />
              </div>
              
              {error && (
                <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}
            </CardContent>
            
            <CardFooter className={`flex flex-col ${isMobile ? 'pt-2' : 'pt-0'}`}>
              <Button 
                type="submit" 
                className={`w-full adalat-button ${isMobile ? 'text-base py-6' : ''}`}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {language === 'ur' ? 'برائے مہربانی انتظار کریں...' : 'Please wait...'}
                  </>
                ) : isLoggingIn ? (
                  language === 'ur' ? 'لاگ ان کریں' : 'Sign In'
                ) : (
                  language === 'ur' ? 'اکاؤنٹ بنائیں' : 'Create Account'
                )}
              </Button>
              
              <p className="mt-4 text-center text-sm text-gray-500">
                {isLoggingIn 
                  ? (language === 'ur' ? 'اکاؤنٹ نہیں ہے؟ ' : "Don't have an account? ")
                  : (language === 'ur' ? 'پہلے سے اکاؤنٹ ہے؟ ' : 'Already have an account? ')}
                <Button
                  variant="link"
                  className="p-0 h-auto text-primary"
                  onClick={() => {
                    setIsLoggingIn(!isLoggingIn);
                    setError(null);
                  }}
                >
                  {isLoggingIn
                    ? (language === 'ur' ? 'اکاؤنٹ بنائیں' : 'Sign up')
                    : (language === 'ur' ? 'لاگ ان کریں' : 'Sign in')}
                </Button>
              </p>
            </CardFooter>
          </form>
        </Tabs>
      </Card>
    </div>
  );
};

export default Auth;
